/**
 * useWebhookDeliveries — W2.5
 *
 * Pagy-paginierte Delivery-Historie für einen Webhook.
 * GET /api/v2/webhooks/:id/deliveries?page=N&success=false
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type WebhookDelivery = {
  id: number
  event_type: string
  http_status: number | null
  success: boolean
  attempt_number: number
  duration_ms: number | null
  // CONTRACT-02/SEC-BE-09: Das BE liefert bewusst NICHT den rohen Response-Body
  // des Zielsystems aus (Read-SSRF ueber eine frei waehlbare Webhook-URL),
  // sondern nur die serverseitig erzeugte Fehlermeldung.
  error_message: string | null
  created_at: string
}

export type PagyMeta = {
  page: number
  per_page: number
  count: number
  pages: number
}

type DeliveriesResponse = {
  data: WebhookDelivery[]
  meta: PagyMeta
}

// --- Hook ---

type UseWebhookDeliveriesParams = {
  webhookId: number
  page: number
  onlyFailures: boolean
}

type UseWebhookDeliveriesResult = {
  deliveries: WebhookDelivery[]
  meta: PagyMeta | null
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useWebhookDeliveries({
  webhookId,
  page,
  onlyFailures,
}: UseWebhookDeliveriesParams): UseWebhookDeliveriesResult {
  const query = useQuery<DeliveriesResponse>({
    queryKey: ['webhook-deliveries', webhookId, page, onlyFailures],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const params: Record<string, unknown> = { page }
      if (onlyFailures) {
        params.success = false
      }
      const result = await client.GET(`/api/v2/webhooks/${webhookId}/deliveries`, {
        params: { query: params },
      })
      if (result.error) {
        throw result.error
      }
      return result.data as DeliveriesResponse
    },
  })

  return {
    deliveries: query.data?.data ?? [],
    meta: query.data?.meta ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
